// ============================================================================
// HORAIRES & JOURNÉE FISCALE — le seul réglage saisi de la journée, et ce qu'il commande.
//
// ⚠️ LA CARTE PORTE AUSSI LE CALENDRIER DES ENVOIS (jours d'ouverture et fuseau), dans l'ordre du dessin — mais
//    AVEC SON PROPRE ENREGISTREMENT (décision utilisateur 2026-10-03 : « une carte, deux enregistrements ») : les heures
//    déplacent la journée fiscale, le calendrier l'heure des envois, et l'échec de l'un ne bloque pas l'autre.
//
// ⚠️ LA BASCULE EST EN LECTURE, JAMAIS UN CHAMP. Elle décrit le MÊME fait que l'heure de fermeture ;
// offrir les deux à la saisie ferait deux valeurs qui divergent au premier oubli, et l'écart ne se
// verrait nulle part — la caisse clôturant à minuit pendant qu'un magasin vend encore.
//
// ⚠️ L'ENREGISTREMENT EST UN GESTE EXPLICITE, ET CONFIRMÉ (décision utilisateur 2026-09-07 : « c'est
// pas quelque chose qui se fait comme ça à la volée »). Ni la frappe, ni la perte du focus
// n'enregistrent : il faut cliquer, puis confirmer. Motif : ces deux champs commandent la BASCULE
// FISCALE de toute la flotte du magasin — une valeur posée par inadvertance déplacerait la frontière
// des journées comptables. La confirmation nomme la conséquence, y compris la bascule qui en découle.
// ============================================================================
import { useEffect, useState, type CSSProperties } from 'react';
import { confirm, EmptyState, Icon, Trans } from '@orgazone/ui-web';
import { deriveDayCutoff, isDayTime } from '@orgazone/core-nf525';
import type { StoreCalendar, StoreHours } from '../api';
import { CalendarSection } from './magasin-calendrier';
import { t } from '../i18n';

/** La carte « Horaires & journée fiscale » : le calendrier des envois puis les horaires, chacun avec son enregistrement. */
export function CarteHoraires({ horaires, onEnregistrer, calendar, onSaveCalendar }: {
  horaires: StoreHours | null;
  onEnregistrer: (open: string, close: string) => void | Promise<void>;
  calendar: StoreCalendar | null;
  onSaveCalendar: (timezone: string, openDays: number[]) => Promise<StoreCalendar>;
}) {
  const [open, setOpen] = useState('');
  const [close, setClose] = useState('');
  // Les champs suivent le serveur — sinon la réponse d'un enregistrement écraserait une saisie
  // en cours, et l'opérateur verrait ses chiffres changer sous ses doigts.
  useEffect(() => {
    if (horaires) { setOpen(horaires.open ?? ''); setClose(horaires.close ?? ''); }
  }, [horaires]);

  const o = open.trim(), c = close.trim();
  const modifie = o !== (horaires?.open ?? '') || c !== (horaires?.close ?? '');
  const saisieValide = isDayTime(o) && isDayTime(c) && o !== c;
  /**
   * La bascule que ces horaires produiront — CALCULÉE PAR LE MÊME CŒUR que le serveur.
   * ⚠️ La confirmation doit annoncer la conséquence exacte : la recomposer ici « à peu près »
   * donnerait une phrase qui promet une bascule et un serveur qui en pose une autre.
   */
  const cutFutur = saisieValide ? deriveDayCutoff(o, c) : null;

  const valider = async () => {
    if (!modifie || !saisieValide || !cutFutur) return;
    const change = cutFutur !== horaires?.cut;
    const oui = await confirm({
      title: t('Enregistrer les horaires {o} → {c} ?', { o, c }),
      message: change
        // ⚠️ Ce que la confirmation doit dire, c'est la conséquence FISCALE — pas « êtes-vous sûr ».
        ? t('La bascule de journée fiscale passera de {avant} à {apres}. Elle ne prendra effet qu’à la prochaine journée, au prochain Z : la journée en cours garde la sienne. Les horaires, eux, descendent tout de suite sur les tickets des caisses.', { avant: horaires?.cut ?? '00:00', apres: cutFutur })
        : t('La bascule de journée fiscale ne change pas. Les horaires descendront sur les tickets des caisses.'),
      confirmLabel: t('Enregistrer'),
      tone: change ? 'danger' : 'info',
    });
    if (oui) void onEnregistrer(o, c);
  };

  const champ: CSSProperties = {
    width: 66, height: 34, padding: '0 9px', border: '1px solid var(--ink-200)',
    borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontFamily: 'var(--font-mono)',
    fontSize: 'var(--t-body)', fontWeight: 600, textAlign: 'center', color: 'var(--ink-900)',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Horaires & journée fiscale')}</div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>
          {/* ⚠️ LE DESSIN DIT « calent le dashboard » — ce n'est plus vrai (arbitrage 2026-09-07) :
              le graphe suit les ventes. L'écran dit donc ce que les horaires font RÉELLEMENT. */}

          {t('Imprimés sur le ticket, et ils commandent la bascule — un magasin qui ferme à 2 h ne clôture pas à minuit.')}
        </div>
      </div>

      {/* L'ORDRE DU DESSIN : jours d'ouverture et fuseau d'abord, puis les horaires et la bascule. */}
      <CalendarSection calendrier={calendar} onEnregistrer={onSaveCalendar} />

      {horaires === null ? (
        // ⚠️ Ni champs vides, ni valeurs par défaut : une saisie offerte sur une lecture ratée
        //    écraserait des horaires existants par ce qu'on croit être « rien ».
        <EmptyState icon="alert" title={t('Horaires non lisibles')}
          note={t('La lecture a échoué — mieux vaut ne rien saisir tant qu’on ne sait pas ce qui est déclaré.')} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 170 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{t('Horaires d’ouverture')}</span>
              {/* ⚠️ ÉCART ASSUMÉ AVEC LE DESSIN, qui annonce « Bornes du graphique horaire ». NI le
                  graphe NI les rapports ne s'y bornent (arbitrage utilisateur 2026-09-07) : ils
                  suivent les VENTES, sinon un magasin qui ouvre exceptionnellement verrait
                  disparaître les ventes hors plage — précisément celles qu'on vient regarder. Ce que
                  les horaires font vraiment : ils commandent la bascule, et s'impriment au ticket. */}
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1 }}>

                {t('Imprimés sur le ticket de caisse — le graphe et les rapports, eux, suivent les ventes réelles')}
              </span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <input value={open} onChange={(e) => setOpen(e.target.value)}
                aria-label={t('Heure d’ouverture')} placeholder="09:00" style={champ} />
              <span aria-hidden style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>→</span>
              <input value={close} onChange={(e) => setClose(e.target.value)}
                aria-label={t('Heure de fermeture')} placeholder="19:30" style={champ} />
            </span>
          </div>

          {horaires.night && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', background: 'var(--amber-bg)' }}>
              <Icon name="moon" size={14} stroke={2.2} color="var(--amber-fg)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>

                {t('Fermeture après minuit — la bascule suit automatiquement : les ventes de fin de nuit restent sur la journée entamée.')}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 170 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{t('Bascule de journée fiscale')}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1 }}>

                {t('Calculée des horaires : minuit — ou l’heure de fermeture quand le magasin ferme après minuit. Z et sessions suivent')}
              </span>
            </span>
            <span style={{ height: 34, padding: '0 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', display: 'inline-flex', alignItems: 'center', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)' }}>
              {horaires.cut}
            </span>
          </div>

          {/* ⚠️ UNE ATTENTE SE DIT, ET DIT QUAND. Un simple « enregistré » sur une bascule qui n'agira
              que cette nuit laisserait croire que la caisse a déjà changé de journée. */}
          {horaires.cutPending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', background: 'var(--amber-bg)' }}>
              <Icon name="clock" size={14} stroke={2.2} color="var(--amber-fg)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
                {/* ⚠️ LA CONSÉQUENCE VISIBLE EST NOMMÉE (retour utilisateur 2026-09-07 : « le
                    dashboard suit toujours de minuit à minuit, pourquoi le chart ne s'adapte pas ? »).
                    Le graphe horaire se lit dans l'ordre de la journée FISCALE : tant que la bascule
                    attend, il reste calendaire — et personne ne pouvait le deviner depuis ici. */}

                {horaires.effectiveAt
                  ? <Trans k='Nouvelle bascule <1>{v}</1> en attente — elle prendra effet le {jour}, au prochain Z. La journée en cours garde la sienne, et le graphe horaire du tableau de bord ne se recalera qu’à ce moment-là.'
                      vars={{ v: horaires.cutPending, jour: jourLisible(horaires.effectiveAt) }} parts={[(x) => <strong>{x}</strong>]} />
                  : <Trans k='Nouvelle bascule <1>{v}</1> en attente — elle prendra effet au prochain Z. La journée en cours garde la sienne, et le graphe horaire du tableau de bord ne se recalera qu’à ce moment-là.'
                      vars={{ v: horaires.cutPending }} parts={[(x) => <strong>{x}</strong>]} />}
              </span>
            </div>
          )}

          {/* ⚠️ UN BANDEAU D'ACTION, PAS UN CHAMP QUI S'ENREGISTRE SEUL. Le bouton reste inerte tant
              que rien n'a changé — un « Enregistrer » toujours cliquable invite à confirmer une
              écriture qui n'a pas lieu d'être, et la boîte de confirmation perd son sens à force. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 140, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              {!modifie ? t('Aucune modification à enregistrer.')
                : !saisieValide ? t('Horaires attendus en HH:MM, et différents l’un de l’autre.')
                  : cutFutur !== horaires.cut
                    ? t('Enregistrer déplacera la bascule sur {v}, à la prochaine journée.', { v: cutFutur ?? '' })
                    : t('La bascule ne changera pas.')}
            </span>
            <button type="button" onClick={() => { void valider(); }} disabled={!modifie || !saisieValide}
              style={{
                height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)',
                background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 600,
                whiteSpace: 'nowrap', cursor: modifie && saisieValide ? 'pointer' : 'not-allowed',
                opacity: modifie && saisieValide ? 1 : 0.5,
              }}>{t('Enregistrer les horaires')}</button>
          </div>

          <div style={{ padding: '10px 16px', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
            <Trans k='Verrou NF525 : la continuité prime — un changement de bascule ne prend effet qu’<1>au prochain Z</1>, jamais rétroactivement (deux journées ne se chevauchent jamais). L’heure elle-même est libre.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></div>
        </>
      )}
    </div>
  );
}

/**
 * `AAAAMMJJHHMMSS` → « 08/09 à 03:00 ».
 * ⚠️ Composé à la main, pas par `toLocaleString` : l'ICU embarquée dans certains environnements
 * retombe silencieusement sur l'anglais, et l'écran afficherait une date qui n'est pas la nôtre —
 * le défaut déjà rencontré sur le procès-verbal d'inventaire.
 */
export function jourLisible(ymdhms: string): string {
  if (!/^\d{14}$/.test(ymdhms)) return ymdhms;
  return `${ymdhms.slice(6, 8)}/${ymdhms.slice(4, 6)} à ${ymdhms.slice(8, 10)}:${ymdhms.slice(10, 12)}`;
}
